<template>
    <ActionReviewDocuments
        :documents="documents"
        :storage-id="storageId"
        :fixed-left="documentsPanelLeft"
        :fixed-width="documentsPanelWidth"
        :fixed-top="documentsFixedTop"
        :fixed-bottom-offset="bottomOffset"
    />
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { DocumentDataReviewDocument } from '@evidentid/tprm-portal-lib/models/document-data-review';

    import ActionReviewDocuments from '@/components/action-review-documents/ActionReviewDocuments.vue';

    export default defineComponent({
        name: 'TrackedActionReviewDocuments',
        components: {
            ActionReviewDocuments,
        },
        props: {
            documents: {
                type: Array as PropType<DocumentDataReviewDocument[]>,
                default: (): DocumentDataReviewDocument[] => [] as DocumentDataReviewDocument[],
            },
            storageId: {
                type: String as PropType<string>,
                default: '',
            },
            anchorTarget: {
                default: null,
                validator: (value: unknown) =>
                    value === null ||
                    typeof HTMLElement === 'undefined' ||
                    value instanceof HTMLElement,
            },
            pageTopOffset: {
                type: Number as PropType<number>,
                default: 0,
            },
            anchorGap: {
                type: Number as PropType<number>,
                default: 0,
            },
            bottomOffset: {
                type: Number as PropType<number>,
                default: 21,
            },
        },
        emits: [ 'position-change' ],
        data() {
            return {
                documentsFixedTop: (this as any).pageTopOffset,
                documentsPanelLeft: 0,
                documentsPanelWidth: 0,
                scrollContainer: null as HTMLElement | null,
                panelResizeObserver: null as ResizeObserver | null,
                anchorResizeObserver: null as ResizeObserver | null,
                rafId: null as number | null, // requestAnimationFrame ID
            };
        },
        watch: {
            anchorTarget: {
                handler() {
                    this.syncAnchorResizeObserver();
                    this.updateDocumentsPosition();
                },
                immediate: false,
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.setupScrollTracking();
                this.setupPanelResizeObserver();
                this.syncAnchorResizeObserver();
                this.updateDocumentsPosition();
            });
        },
        beforeUnmount() {
            if (this.panelResizeObserver) {
                this.panelResizeObserver.disconnect();
                this.panelResizeObserver = null;
            }
            if (this.anchorResizeObserver) {
                this.anchorResizeObserver.disconnect();
                this.anchorResizeObserver = null;
            }
            if (this.scrollContainer) {
                this.scrollContainer.removeEventListener('scroll', this.onScroll);
                this.scrollContainer = null;
            }
            if (this.rafId !== null) {
                cancelAnimationFrame(this.rafId);
                this.rafId = null;
            }
        },
        methods: {
            emitPositionChange(top: number): void {
                this.$emit('position-change', top);
            },
            setupScrollTracking(): void {
                if (this.scrollContainer) {
                    return;
                }

                const scrollContainer = this.$el?.closest('.PageWrapper') as HTMLElement | null;
                if (scrollContainer) {
                    this.scrollContainer = scrollContainer;
                    this.scrollContainer.addEventListener('scroll', this.onScroll, { passive: true });
                }
            },
            setupPanelResizeObserver(): void {
                if (this.panelResizeObserver || typeof ResizeObserver === 'undefined') {
                    return;
                }

                const beforePanel = this.$el?.closest('.q-splitter__before') as HTMLElement | null;
                if (!beforePanel) {
                    return;
                }

                this.panelResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                    requestAnimationFrame(() => {
                        const entry = entries[0];
                        if (entry) {
                            this.documentsPanelLeft = entry.target.getBoundingClientRect().left;
                            this.documentsPanelWidth = entry.contentRect.width;
                        }
                    });
                });
                this.panelResizeObserver.observe(beforePanel);
                this.documentsPanelLeft = beforePanel.getBoundingClientRect().left;
                this.documentsPanelWidth = beforePanel.getBoundingClientRect().width;
            },
            syncAnchorResizeObserver(): void {
                if (this.anchorResizeObserver) {
                    this.anchorResizeObserver.disconnect();
                    this.anchorResizeObserver = null;
                }

                if (typeof ResizeObserver === 'undefined' || !(this as any).anchorTarget) {
                    return;
                }

                this.anchorResizeObserver = new ResizeObserver(() => {
                    requestAnimationFrame(() => {
                        this.updateDocumentsPosition();
                    });
                });
                this.anchorResizeObserver.observe((this as any).anchorTarget);
            },
            onScroll(): void {
                if (this.rafId !== null) {
                    return;
                }

                this.rafId = requestAnimationFrame(() => {
                    this.updateDocumentsPosition();
                    this.rafId = null;
                });
            },
            updateDocumentsPosition(): void {
                const anchorTarget = (this as any).anchorTarget as HTMLElement | null;
                if (!anchorTarget) {
                    return;
                }

                const nextTop = Math.max(
                    anchorTarget.getBoundingClientRect().bottom + (this as any).anchorGap,
                    (this as any).pageTopOffset,
                );

                this.documentsFixedTop = nextTop;
                this.emitPositionChange(nextTop);
            },
        },
    });
</script>
